import React, { useState, useEffect } from 'react';
import FileUploadZone from './FileUploadZone';
import { apiRequest } from '../../../services/api';
import { getCurrentUser } from '../../../services/authService';

const ACCEPTED_DOC_TYPES = 'application/pdf,image/jpeg,image/jpg,image/png';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const Step4Documents = ({ guideId: guideIdProp, onNext }) => {
  const [guideId, setGuideId] = useState(guideIdProp || null);
  const [resolvingGuideId, setResolvingGuideId] = useState(!guideIdProp);
  const [idDocFiles, setIdDocFiles] = useState([]);
  const [certFiles, setCertFiles] = useState([]);
  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [idError, setIdError] = useState('');
  const [certError, setCertError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (guideIdProp) {
      setGuideId(guideIdProp);
      setResolvingGuideId(false);
      return;
    }
    setResolvingGuideId(true);
    getCurrentUser()
      .then((user) => {
        if (user?.id) { setGuideId(user.id); }
      })
      .catch(() => {})
      .finally(() => { setResolvingGuideId(false); });
  }, [guideIdProp]);

  // Load previously uploaded documents when guideId is resolved
  useEffect(() => {
    if (!guideId) { return; }
    apiRequest(`/api/v1/assets/entity/guide/${guideId}?category=document`, { method: 'GET' })
      .then((res) => {
        const assets = Array.isArray(res?.data) ? res.data : [];
        setIdDocFiles(assets
          .filter(a => a.asset_purpose === 'id_document')
          .map(a => ({ id: a.id, name: a.original_filename || 'Document', size: a.file_size_bytes }))
        );
        setCertFiles(assets
          .filter(a => a.asset_purpose === 'certification')
          .map(a => ({ id: a.id, name: a.original_filename || 'Document', size: a.file_size_bytes }))
        );
      })
      .catch(() => {});
  }, [guideId]);

  const validateFileType = (file) => {
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    return allowed.includes(file.type);
  };

  const uploadFile = async (file, assetPurpose, setUploading, setError, setFiles) => {
    if (!validateFileType(file)) {
      setError('Only PDF, JPG, and PNG files are accepted.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File exceeds 10MB limit.');
      return;
    }
    if (!guideId) {
      setError('Unable to determine your guide ID. Please refresh and try again.');
      return;
    }

    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'guide');
      formData.append('entityId', guideId);
      formData.append('category', 'document');
      formData.append('assetPurpose', assetPurpose);

      const response = await apiRequest('/api/v1/assets/upload', {
        method: 'POST',
        body: formData,
      });

      const asset = response?.data?.asset ?? response?.data ?? response;
      setFiles((prev) => [
        ...prev,
        { id: asset.id, name: file.name, size: file.size },
      ]);
    } catch (err) {
      setError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleIdDocSelect = (files) => {
    const file = files[0];
    if (!file) { return; }
    uploadFile(file, 'id_document', setUploadingId, setIdError, setIdDocFiles);
  };

  const handleCertSelect = (files) => {
    for (const file of files) {
      uploadFile(file, 'certification', setUploadingCert, setCertError, setCertFiles);
    }
  };

  const handleRemoveIdDoc = (id) => {
    setIdDocFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleRemoveCert = (id) => {
    setCertFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const canContinue = idDocFiles.length > 0 && !uploadingId && !uploadingCert;

  const handleContinue = async () => {
    if (!canContinue) { return; }
    setSubmitError('');
    setSubmitting(true);
    try {
      await apiRequest('/api/v1/guides/signup/progress', {
        method: 'PATCH',
        body: { signupStage: 4 },
      });
      if (onNext) { onNext(); }
    } catch (err) {
      setSubmitError(err?.message || 'Failed to save progress. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (resolvingGuideId) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Documents</h2>
      <p className="text-sm text-gray-500 mb-8">
        We need to verify your identity before you can start guiding.
      </p>

      <div className="space-y-8">
        {/* Government ID — required */}
        <section>
          <div className="flex items-center mb-1">
            <h3 className="text-base font-semibold text-gray-800">Government ID</h3>
            <span className="ml-2 text-xs font-medium text-red-500 uppercase tracking-wide">Required</span>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Driver&apos;s license, passport, or national ID card.
          </p>
          <FileUploadZone
            label=""
            description="Click or drag and drop your government ID"
            acceptedTypes={ACCEPTED_DOC_TYPES}
            maxSize={MAX_FILE_SIZE}
            multiple={false}
            uploading={uploadingId}
            error={idError}
            uploadedFiles={idDocFiles}
            onRemoveFile={handleRemoveIdDoc}
            onFilesSelect={handleIdDocSelect}
          />
        </section>

        {/* Certifications — optional */}
        <section>
          <div className="flex items-center mb-1">
            <h3 className="text-base font-semibold text-gray-800">Certifications</h3>
            <span className="ml-2 text-xs font-medium text-gray-400 uppercase tracking-wide">Optional</span>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Guiding certifications, first aid, or other relevant credentials.
          </p>
          <FileUploadZone
            label=""
            description="Click or drag and drop certification documents"
            acceptedTypes={ACCEPTED_DOC_TYPES}
            maxSize={MAX_FILE_SIZE}
            multiple={true}
            uploading={uploadingCert}
            error={certError}
            uploadedFiles={certFiles}
            onRemoveFile={handleRemoveCert}
            onFilesSelect={handleCertSelect}
          />
        </section>
      </div>

      {submitError && (
        <p className="mt-4 text-sm text-red-600">{submitError}</p>
      )}

      <div className="mt-8">
        <button
          type="button"
          onClick={handleContinue}
          disabled={!canContinue || submitting}
          className={`
            w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors
            ${canContinue && !submitting
              ? 'bg-emerald-600 hover:bg-emerald-700'
              : 'bg-gray-300 cursor-not-allowed'}
          `}
        >
          {submitting ? 'Saving…' : 'Continue'}
        </button>
        {idDocFiles.length === 0 && (
          <p className="mt-2 text-xs text-center text-gray-400">
            Upload your government ID to continue
          </p>
        )}
      </div>
    </div>
  );
};

export default Step4Documents;
